import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Alert,
  Snackbar,
  Grid,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Domain as DomainIcon,
  Refresh as RefreshIcon,
  Dns as DnsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from "@mui/icons-material";
import { DashboardLayout } from "../app/layout/DashboardLayout";
import { tenantsAPI } from "../services/api";

interface Tenant {
  tenant_id: string;
  company_name: string;
  domain?: string;
  subdomain?: string;
  subscription_plan: "free" | "basic" | "pro" | "enterprise";
  branding: {
    logo_url?: string;
    primary_color?: string;
    secondary_color?: string;
    company_description?: string;
  };
  settings: {
    timezone: string;
    currency: string;
    language: string;
    order_number_prefix?: string;
    customer_number_prefix?: string;
  };
  is_active: boolean;
  dns_status?: "pending" | "active" | "inactive" | "error";
  full_domain?: string;
  dns_record_id?: string;
  created_at: string;
  updated_at: string;
}

interface CreateTenantForm {
  company_name: string;
  subdomain: string;
  subscription_plan: "free" | "basic" | "pro" | "enterprise";
  primary_color: string;
  secondary_color: string;
  timezone: string;
  currency: string;
  language: string;
  order_number_prefix: string;
  customer_number_prefix: string;
  is_active: boolean;
}

const TenantsManagement: React.FC = () => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage] = useState(1);
  const [dnsProcessing, setDnsProcessing] = useState<string | null>(null);

  // Load tenants from API
  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tenantsAPI.getTenants({
        page: currentPage,
        limit: 10,
      });
      setTenants(response.data || []);
    } catch (error: any) {
      console.error("Failed to load tenants:", error);
      setAlertMessage(
        error.message || "Tenant listesi yüklenirken hata oluştu"
      );
      setShowErrorAlert(true);
      setTenants([]); // Set empty array instead of mock data
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  // Load tenants on component mount
  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const [formData, setFormData] = useState<CreateTenantForm>({
    company_name: "",
    subdomain: "",
    subscription_plan: "basic",
    primary_color: "#1976d2",
    secondary_color: "#dc004e",
    timezone: "Europe/Istanbul",
    currency: "TRY",
    language: "tr",
    order_number_prefix: "ORD",
    customer_number_prefix: "CUST",
    is_active: true,
  });

  const handleInputChange = (field: keyof CreateTenantForm, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddTenant = async () => {
    try {
      setLoading(true);

      const tenantData = {
        company_name: formData.company_name,
        subdomain: formData.subdomain,
        subscription_plan: formData.subscription_plan,
        branding: {
          primary_color: formData.primary_color,
          secondary_color: formData.secondary_color,
          company_description: `${formData.company_name} için sipariş yönetim sistemi`,
        },
        settings: {
          timezone: formData.timezone,
          currency: formData.currency,
          language: formData.language,
          order_number_prefix: formData.order_number_prefix,
          customer_number_prefix: formData.customer_number_prefix,
        },
      };

      await tenantsAPI.createTenant(tenantData);

      setShowAddDialog(false);
      setAlertMessage("Tenant başarıyla oluşturuldu!");
      setShowSuccessAlert(true);

      // Reset form
      setFormData({
        company_name: "",
        subdomain: "",
        subscription_plan: "basic",
        primary_color: "#1976d2",
        secondary_color: "#dc004e",
        timezone: "Europe/Istanbul",
        currency: "TRY",
        language: "tr",
        order_number_prefix: "ORD",
        customer_number_prefix: "CUST",
        is_active: true,
      });

      // Reload tenants
      await loadTenants();
    } catch (error: any) {
      console.error("Failed to create tenant:", error);
      setAlertMessage(error.message || "Tenant oluşturulurken hata oluştu");
      setShowErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      company_name: tenant.company_name,
      subdomain: tenant.subdomain || "",
      subscription_plan: tenant.subscription_plan,
      primary_color: tenant.branding.primary_color || "#1976d2",
      secondary_color: tenant.branding.secondary_color || "#dc004e",
      timezone: tenant.settings.timezone,
      currency: tenant.settings.currency,
      language: tenant.settings.language,
      order_number_prefix: tenant.settings.order_number_prefix || "ORD",
      customer_number_prefix: tenant.settings.customer_number_prefix || "CUST",
      is_active: tenant.is_active,
    });
    setShowEditDialog(true);
  };

  const handleUpdateTenant = async () => {
    if (selectedTenant) {
      try {
        setLoading(true);

        const tenantData = {
          company_name: formData.company_name,
          subdomain: formData.subdomain,
          subscription_plan: formData.subscription_plan,
          branding: {
            ...selectedTenant.branding,
            primary_color: formData.primary_color,
            secondary_color: formData.secondary_color,
            company_description: `${formData.company_name} için sipariş yönetim sistemi`,
          },
          settings: {
            timezone: formData.timezone,
            currency: formData.currency,
            language: formData.language,
            order_number_prefix: formData.order_number_prefix,
            customer_number_prefix: formData.customer_number_prefix,
          },
          is_active: formData.is_active,
        };

        await tenantsAPI.updateTenant(selectedTenant.tenant_id, tenantData);

        setShowEditDialog(false);
        setSelectedTenant(null);
        setAlertMessage("Tenant başarıyla güncellendi!");
        setShowSuccessAlert(true);

        // Reload tenants
        await loadTenants();
      } catch (error: any) {
        console.error("Failed to update tenant:", error);
        setAlertMessage(error.message || "Tenant güncellenirken hata oluştu");
        setShowErrorAlert(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteTenant = async (tenantId: string) => {
    if (
      window.confirm(
        "Bu tenant'ı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve tüm veriler silinecektir."
      )
    ) {
      try {
        setLoading(true);
        await tenantsAPI.deleteTenant(tenantId);
        setAlertMessage("Tenant başarıyla silindi!");
        setShowSuccessAlert(true);

        // Reload tenants
        await loadTenants();
      } catch (error: any) {
        console.error("Failed to delete tenant:", error);
        setAlertMessage(error.message || "Tenant silinirken hata oluştu");
        setShowErrorAlert(true);
      } finally {
        setLoading(false);
      }
    }
  };

  // DNS durum kontrol fonksiyonu
  const handleCheckDNSStatus = async (tenantId: string) => {
    try {
      setDnsProcessing(tenantId);
      const result = await tenantsAPI.checkTenantDNSStatus(tenantId);

      if (result.success) {
        setAlertMessage(`DNS durumu kontrol edildi: ${result.data.dns_status}`);
        setShowSuccessAlert(true);

        // Tenant listesini güncelle
        await loadTenants();
      } else {
        setAlertMessage("DNS durumu kontrol edilemedi");
        setShowErrorAlert(true);
      }
    } catch (error: any) {
      console.error("DNS status check failed:", error);
      setAlertMessage(error.message || "DNS durumu kontrol edilemedi");
      setShowErrorAlert(true);
    } finally {
      setDnsProcessing(null);
    }
  };

  // Wildcard DNS kurulumu
  const handleSetupWildcardDNS = async () => {
    if (
      window.confirm(
        "Wildcard DNS kaydı oluşturulsun mu? Bu işlem tüm subdomain'leri otomatik olarak aktif hale getirir."
      )
    ) {
      try {
        setLoading(true);
        const result = await tenantsAPI.setupWildcardDNS();

        if (result.success) {
          setAlertMessage(
            "Wildcard DNS başarıyla kuruldu! Tüm subdomain'ler artık otomatik aktif."
          );
          setShowSuccessAlert(true);
          await loadTenants();
        } else {
          setAlertMessage(
            result.data?.error || "Wildcard DNS kurulumu başarısız"
          );
          setShowErrorAlert(true);
        }
      } catch (error: any) {
        console.error("Wildcard DNS setup failed:", error);
        setAlertMessage(error.message || "Wildcard DNS kurulumu başarısız");
        setShowErrorAlert(true);
      } finally {
        setLoading(false);
      }
    }
  };

  // Mevcut tenant'lar için DNS kurulumu
  const handleSetupExistingDNS = async () => {
    if (
      window.confirm(
        "Mevcut tüm tenant'lar için DNS kayıtları oluşturulsun mu?"
      )
    ) {
      try {
        setLoading(true);
        const result = await tenantsAPI.setupDNSForExistingTenants();

        if (result.success) {
          const successCount = result.data.filter((r: any) => r.success).length;
          setAlertMessage(
            `${successCount} tenant için DNS kayıtları başarıyla oluşturuldu`
          );
          setShowSuccessAlert(true);
          await loadTenants();
        } else {
          setAlertMessage("DNS kayıtları oluşturulamadı");
          setShowErrorAlert(true);
        }
      } catch (error: any) {
        console.error("DNS setup failed:", error);
        setAlertMessage(error.message || "DNS kayıtları oluşturulamadı");
        setShowErrorAlert(true);
      } finally {
        setLoading(false);
      }
    }
  };

  // DNS durumu için icon
  const getDNSStatusIcon = (status?: string) => {
    switch (status) {
      case "active":
        return <CheckCircleIcon color="success" />;
      case "inactive":
      case "error":
        return <ErrorIcon color="error" />;
      case "pending":
        return <HourglassEmptyIcon color="warning" />;
      default:
        return <HourglassEmptyIcon color="disabled" />;
    }
  };

  // DNS durumu için renk
  const getDNSStatusColor = (status?: string) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
      case "error":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const getSubscriptionPlanColor = (plan: string) => {
    switch (plan) {
      case "enterprise":
        return "error";
      case "pro":
        return "warning";
      case "basic":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <DashboardLayout title="Tenant Yönetimi">
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h4" component="h1">
              Tenant Yönetimi
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadTenants}
                disabled={loading}
              >
                Yenile
              </Button>
              <Button
                variant="outlined"
                startIcon={<DnsIcon />}
                onClick={handleSetupWildcardDNS}
                disabled={loading}
                color="primary"
              >
                Wildcard DNS Kur
              </Button>
              <Button
                variant="outlined"
                startIcon={<DnsIcon />}
                onClick={handleSetupExistingDNS}
                disabled={loading}
                color="secondary"
              >
                Mevcut DNS Kur
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddDialog(true)}
                disabled={loading}
              >
                Yeni Tenant Ekle
              </Button>
            </Box>
          </Box>

          <Card>
            <CardContent>
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              )}

              {!loading && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Şirket Adı</TableCell>
                        <TableCell>Subdomain</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Durum</TableCell>
                        <TableCell>DNS Durumu</TableCell>
                        <TableCell>Oluşturulma Tarihi</TableCell>
                        <TableCell>İşlemler</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tenants.map((tenant) => (
                        <TableRow key={tenant.tenant_id}>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Avatar
                                sx={{
                                  mr: 2,
                                  bgcolor: tenant.branding.primary_color,
                                }}
                              >
                                <BusinessIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="body1" fontWeight="bold">
                                  {tenant.company_name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {tenant.branding.company_description}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.subdomain}
                              icon={<DomainIcon />}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.subscription_plan.toUpperCase()}
                              color={
                                getSubscriptionPlanColor(
                                  tenant.subscription_plan
                                ) as any
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.is_active ? "Aktif" : "Pasif"}
                              color={tenant.is_active ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Chip
                                icon={getDNSStatusIcon(tenant.dns_status)}
                                label={
                                  tenant.dns_status?.toUpperCase() || "UNKNOWN"
                                }
                                color={
                                  getDNSStatusColor(tenant.dns_status) as any
                                }
                                size="small"
                              />
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleCheckDNSStatus(tenant.tenant_id)
                                }
                                disabled={dnsProcessing === tenant.tenant_id}
                                title="DNS Durumunu Kontrol Et"
                              >
                                {dnsProcessing === tenant.tenant_id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <RefreshIcon />
                                )}
                              </IconButton>
                            </Box>
                            {tenant.full_domain && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                              >
                                {tenant.full_domain}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {new Date(tenant.created_at).toLocaleDateString(
                              "tr-TR"
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="primary"
                              onClick={() => handleEditTenant(tenant)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleDeleteTenant(tenant.tenant_id)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Add Tenant Dialog */}
        <Dialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Yeni Tenant Ekle</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Şirket Adı"
                  value={formData.company_name}
                  onChange={(e) =>
                    handleInputChange("company_name", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subdomain"
                  value={formData.subdomain}
                  onChange={(e) =>
                    handleInputChange("subdomain", e.target.value)
                  }
                  helperText="Örnek: demo, client1"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Abonelik Planı</InputLabel>
                  <Select
                    value={formData.subscription_plan}
                    onChange={(e) =>
                      handleInputChange("subscription_plan", e.target.value)
                    }
                  >
                    <MenuItem value="free">Ücretsiz</MenuItem>
                    <MenuItem value="basic">Temel</MenuItem>
                    <MenuItem value="pro">Pro</MenuItem>
                    <MenuItem value="enterprise">Kurumsal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) =>
                        handleInputChange("is_active", e.target.checked)
                      }
                    />
                  }
                  label="Aktif"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ana Renk"
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) =>
                    handleInputChange("primary_color", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="İkincil Renk"
                  type="color"
                  value={formData.secondary_color}
                  onChange={(e) =>
                    handleInputChange("secondary_color", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Zaman Dilimi</InputLabel>
                  <Select
                    value={formData.timezone}
                    onChange={(e) =>
                      handleInputChange("timezone", e.target.value)
                    }
                  >
                    <MenuItem value="Europe/Istanbul">İstanbul</MenuItem>
                    <MenuItem value="Europe/London">Londra</MenuItem>
                    <MenuItem value="America/New_York">New York</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Para Birimi</InputLabel>
                  <Select
                    value={formData.currency}
                    onChange={(e) =>
                      handleInputChange("currency", e.target.value)
                    }
                  >
                    <MenuItem value="TRY">TRY</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Dil</InputLabel>
                  <Select
                    value={formData.language}
                    onChange={(e) =>
                      handleInputChange("language", e.target.value)
                    }
                  >
                    <MenuItem value="tr">Türkçe</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sipariş Numarası Öneki"
                  value={formData.order_number_prefix}
                  onChange={(e) =>
                    handleInputChange("order_number_prefix", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Müşteri Numarası Öneki"
                  value={formData.customer_number_prefix}
                  onChange={(e) =>
                    handleInputChange("customer_number_prefix", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddDialog(false)} disabled={loading}>
              İptal
            </Button>
            <Button
              onClick={handleAddTenant}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : "Ekle"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Tenant Dialog */}
        <Dialog
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Tenant Düzenle</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Şirket Adı"
                  value={formData.company_name}
                  onChange={(e) =>
                    handleInputChange("company_name", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subdomain"
                  value={formData.subdomain}
                  onChange={(e) =>
                    handleInputChange("subdomain", e.target.value)
                  }
                  helperText="Örnek: demo, client1"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Abonelik Planı</InputLabel>
                  <Select
                    value={formData.subscription_plan}
                    onChange={(e) =>
                      handleInputChange("subscription_plan", e.target.value)
                    }
                  >
                    <MenuItem value="free">Ücretsiz</MenuItem>
                    <MenuItem value="basic">Temel</MenuItem>
                    <MenuItem value="pro">Pro</MenuItem>
                    <MenuItem value="enterprise">Kurumsal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) =>
                        handleInputChange("is_active", e.target.checked)
                      }
                    />
                  }
                  label="Aktif"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ana Renk"
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) =>
                    handleInputChange("primary_color", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="İkincil Renk"
                  type="color"
                  value={formData.secondary_color}
                  onChange={(e) =>
                    handleInputChange("secondary_color", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Zaman Dilimi</InputLabel>
                  <Select
                    value={formData.timezone}
                    onChange={(e) =>
                      handleInputChange("timezone", e.target.value)
                    }
                  >
                    <MenuItem value="Europe/Istanbul">İstanbul</MenuItem>
                    <MenuItem value="Europe/London">Londra</MenuItem>
                    <MenuItem value="America/New_York">New York</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Para Birimi</InputLabel>
                  <Select
                    value={formData.currency}
                    onChange={(e) =>
                      handleInputChange("currency", e.target.value)
                    }
                  >
                    <MenuItem value="TRY">TRY</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Dil</InputLabel>
                  <Select
                    value={formData.language}
                    onChange={(e) =>
                      handleInputChange("language", e.target.value)
                    }
                  >
                    <MenuItem value="tr">Türkçe</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sipariş Numarası Öneki"
                  value={formData.order_number_prefix}
                  onChange={(e) =>
                    handleInputChange("order_number_prefix", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Müşteri Numarası Öneki"
                  value={formData.customer_number_prefix}
                  onChange={(e) =>
                    handleInputChange("customer_number_prefix", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditDialog(false)} disabled={loading}>
              İptal
            </Button>
            <Button
              onClick={handleUpdateTenant}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : "Güncelle"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Alerts */}
        <Snackbar
          open={showSuccessAlert}
          autoHideDuration={6000}
          onClose={() => setShowSuccessAlert(false)}
        >
          <Alert severity="success" onClose={() => setShowSuccessAlert(false)}>
            {alertMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={showErrorAlert}
          autoHideDuration={6000}
          onClose={() => setShowErrorAlert(false)}
        >
          <Alert severity="error" onClose={() => setShowErrorAlert(false)}>
            {alertMessage}
          </Alert>
        </Snackbar>
      </Container>
    </DashboardLayout>
  );
};

export default TenantsManagement;
